import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FiBookOpen,
  FiPlus,
  FiShare2,
  FiTrash2,
  FiSave,
  FiEdit2,
  FiEye,
  FiFileText,
  FiFolder,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import LoadingButton from '../components/LoadingButton';
import Modal from '../components/Modal';
import { formatLocalDateTime } from '../utils/formatTimestamp';
import { renderMarkdownToHtml } from '../utils/boardMarkdown';
import './Board.css';
import { appAlert, appConfirm } from '../utils/appDialog';

function userLabel(u) {
  const name = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email})` : u?.email || `User #${u?.id}`;
}

const Board = () => {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('board_manage');
  const canShare = hasPermission('board_share');

  const [selectedNotebookId, setSelectedNotebookId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [mode, setMode] = useState('preview'); // edit | preview
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [dirty, setDirty] = useState(false);

  const [showNotebookModal, setShowNotebookModal] = useState(false);
  const [notebookTitle, setNotebookTitle] = useState('');
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [sectionTitle, setSectionTitle] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUserId, setShareUserId] = useState('');
  const [shareAccess, setShareAccess] = useState('view');

  const { data: treeData, isLoading: treeLoading } = useQuery(
    ['board-tree'],
    () => api.get('/board/tree').then((r) => r.data),
    { refetchOnWindowFocus: false }
  );

  const notebooks = useMemo(() => treeData?.notebooks || [], [treeData]);

  const selectedNotebook = useMemo(
    () => notebooks.find((n) => Number(n.id) === Number(selectedNotebookId)) || null,
    [notebooks, selectedNotebookId]
  );

  const selectedSection = useMemo(
    () =>
      selectedNotebook?.sections?.find((s) => Number(s.id) === Number(selectedSectionId)) || null,
    [selectedNotebook, selectedSectionId]
  );

  // Auto-select first notebook/section/page
  useEffect(() => {
    if (!notebooks.length) {
      setSelectedNotebookId(null);
      setSelectedSectionId(null);
      setSelectedPageId(null);
      return;
    }
    if (!selectedNotebookId || !notebooks.some((n) => Number(n.id) === Number(selectedNotebookId))) {
      const n = notebooks[0];
      setSelectedNotebookId(n.id);
      const s = n.sections?.[0];
      setSelectedSectionId(s?.id || null);
      setSelectedPageId(s?.pages?.[0]?.id || null);
      return;
    }
    if (selectedNotebook && (!selectedSectionId || !selectedNotebook.sections?.some((s) => Number(s.id) === Number(selectedSectionId)))) {
      const s = selectedNotebook.sections?.[0];
      setSelectedSectionId(s?.id || null);
      setSelectedPageId(s?.pages?.[0]?.id || null);
    }
  }, [notebooks, selectedNotebookId, selectedNotebook, selectedSectionId]);

  const {
    data: page,
    isLoading: pageLoading,
    isFetching: pageFetching,
  } = useQuery(
    ['board-page', selectedPageId],
    () => api.get(`/board/pages/${selectedPageId}`).then((r) => r.data),
    {
      enabled: Boolean(selectedPageId),
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (!page || dirty) return;
    setDraftTitle(page.title || '');
    setDraftContent(page.content || '');
    setMode(page.access === 'view' ? 'preview' : mode === 'edit' ? 'edit' : 'preview');
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: shares = [] } = useQuery(
    ['board-shares', selectedPageId],
    () => api.get(`/board/pages/${selectedPageId}/shares`).then((r) => r.data),
    {
      enabled: Boolean(selectedPageId && showShareModal && canShare && page?.access === 'owner'),
    }
  );

  const { data: shareableUsers = [] } = useQuery(
    ['board-shareable-users'],
    () => api.get('/board/shareable-users').then((r) => r.data),
    { enabled: showShareModal && canShare, staleTime: 60_000 }
  );

  const invalidateTree = () => queryClient.invalidateQueries(['board-tree']);

  const createNotebookMutation = useMutation(
    (body) => api.post('/board/notebooks', body),
    {
      onSuccess: (res) => {
        invalidateTree();
        setShowNotebookModal(false);
        setNotebookTitle('');
        setSelectedNotebookId(res.data.id);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const createSectionMutation = useMutation(
    ({ notebookId, title }) => api.post(`/board/notebooks/${notebookId}/sections`, { title }),
    {
      onSuccess: (res) => {
        invalidateTree();
        setShowSectionModal(false);
        setSectionTitle('');
        setSelectedSectionId(res.data.id);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const createPageMutation = useMutation(
    ({ sectionId }) =>
      api.post(`/board/sections/${sectionId}/pages`, {
        title: 'Untitled page',
        content: '# Untitled page\n\nStart writing your wiki notes here.\n',
      }),
    {
      onSuccess: (res) => {
        invalidateTree();
        setSelectedPageId(res.data.id);
        setDirty(false);
        setMode('edit');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const savePageMutation = useMutation(
    () =>
      api.put(`/board/pages/${selectedPageId}`, {
        title: draftTitle.trim() || 'Untitled page',
        content: draftContent,
      }),
    {
      onSuccess: (res) => {
        queryClient.setQueryData(['board-page', selectedPageId], res.data);
        setDirty(false);
        invalidateTree();
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deletePageMutation = useMutation(
    () => api.delete(`/board/pages/${selectedPageId}`),
    {
      onSuccess: () => {
        setSelectedPageId(null);
        setDirty(false);
        invalidateTree();
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deleteNotebookMutation = useMutation(
    (id) => api.delete(`/board/notebooks/${id}`),
    {
      onSuccess: () => {
        setSelectedNotebookId(null);
        invalidateTree();
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const addShareMutation = useMutation(
    () =>
      api.post(`/board/pages/${selectedPageId}/shares`, {
        user_id: Number(shareUserId),
        access: shareAccess,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['board-shares', selectedPageId]);
        setShareUserId('');
        setShareAccess('view');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const removeShareMutation = useMutation(
    (userId) => api.delete(`/board/pages/${selectedPageId}/shares/${userId}`),
    {
      onSuccess: () => queryClient.invalidateQueries(['board-shares', selectedPageId]),
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const canEditPage = page && page.access && page.access !== 'view';
  const previewHtml = useMemo(() => renderMarkdownToHtml(draftContent), [draftContent]);

  return (
    <div className="board-page list-page">
      <div className="page-header">
        <div className="page-header-text">
          <h1>
            <FiBookOpen aria-hidden /> Board
          </h1>
          <p className="page-subtitle">
            OneNote-style notebooks for wiki docs. Share individual pages with teammates.
          </p>
        </div>
        <div className="page-toolbar list-page-header-actions">
          {canManage && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowNotebookModal(true)}
            >
              <FiPlus /> New notebook
            </button>
          )}
        </div>
      </div>

      <div className="board-shell">
        <aside className="board-nav">
          <div className="board-nav-head">
            <span>Notebooks</span>
          </div>
          {treeLoading ? (
            <div className="loading board-nav-empty">Loading…</div>
          ) : notebooks.length === 0 ? (
            <div className="board-nav-empty">
              <p>No notebooks yet.</p>
              {canManage && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowNotebookModal(true)}
                >
                  <FiPlus /> Create one
                </button>
              )}
            </div>
          ) : (
            <ul className="board-notebook-list">
              {notebooks.map((n) => (
                <li key={n.id} className={Number(n.id) === Number(selectedNotebookId) ? 'is-active' : ''}>
                  <button
                    type="button"
                    className="board-notebook-btn"
                    onClick={async () => {
                      if (dirty && !await appConfirm('Discard unsaved changes?')) return;
                      setDirty(false);
                      setSelectedNotebookId(n.id);
                      const s = n.sections?.[0];
                      setSelectedSectionId(s?.id || null);
                      setSelectedPageId(s?.pages?.[0]?.id || null);
                    }}
                  >
                    <FiBookOpen aria-hidden />
                    <span>{n.title}</span>
                    {!n.is_owner ? <em className="board-shared-tag">shared</em> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selectedNotebook && (
            <div className="board-sections">
              <div className="board-nav-head">
                <span>Sections</span>
                {canManage && selectedNotebook.is_owner && (
                  <button
                    type="button"
                    className="board-icon-btn"
                    title="Add section"
                    onClick={() => setShowSectionModal(true)}
                  >
                    <FiPlus />
                  </button>
                )}
              </div>
              <ul className="board-section-list">
                {(selectedNotebook.sections || []).map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className={`board-section-btn${Number(s.id) === Number(selectedSectionId) ? ' is-active' : ''}`}
                      onClick={async () => {
                        if (dirty && !await appConfirm('Discard unsaved changes?')) return;
                        setDirty(false);
                        setSelectedSectionId(s.id);
                        setSelectedPageId(s.pages?.[0]?.id || null);
                      }}
                    >
                      <FiFolder aria-hidden />
                      <span>{s.title}</span>
                    </button>
                    {Number(s.id) === Number(selectedSectionId) && (
                      <ul className="board-page-list">
                        {(s.pages || []).map((p) => (
                          <li key={p.id}>
                            <button
                              type="button"
                              className={`board-page-btn${Number(p.id) === Number(selectedPageId) ? ' is-active' : ''}`}
                              onClick={async () => {
                                if (dirty && !await appConfirm('Discard unsaved changes?')) return;
                                setDirty(false);
                                setSelectedPageId(p.id);
                              }}
                            >
                              <FiFileText aria-hidden />
                              <span>{p.title}</span>
                            </button>
                          </li>
                        ))}
                        {canManage && selectedNotebook.is_owner && (
                          <li>
                            <button
                              type="button"
                              className="board-page-btn board-page-btn--add"
                              onClick={() => createPageMutation.mutate({ sectionId: s.id })}
                              disabled={createPageMutation.isLoading}
                            >
                              <FiPlus /> New page
                            </button>
                          </li>
                        )}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        <main className="board-canvas">
          {!selectedPageId ? (
            <div className="board-empty">
              <FiBookOpen aria-hidden />
              <h2>Select or create a page</h2>
              <p>Use notebooks and sections like OneNote. Share pages individually with users.</p>
            </div>
          ) : pageLoading && !page ? (
            <div className="loading">Loading page…</div>
          ) : (
            <>
              <div className="board-canvas-toolbar">
                <div className="board-breadcrumb">
                  <span>{page?.notebook_title || selectedNotebook?.title}</span>
                  <span>/</span>
                  <span>{page?.section_title || selectedSection?.title}</span>
                </div>
                <div className="board-canvas-actions">
                  {canEditPage && (
                    <div className="board-mode-toggle" role="group" aria-label="Editor mode">
                      <button
                        type="button"
                        className={mode === 'edit' ? 'is-active' : ''}
                        onClick={() => setMode('edit')}
                      >
                        <FiEdit2 /> Edit
                      </button>
                      <button
                        type="button"
                        className={mode === 'preview' ? 'is-active' : ''}
                        onClick={() => setMode('preview')}
                      >
                        <FiEye /> Preview
                      </button>
                    </div>
                  )}
                  {canEditPage && (
                    <LoadingButton
                      type="button"
                      className="btn btn-primary btn-sm"
                      icon={FiSave}
                      loading={savePageMutation.isLoading}
                      disabled={!dirty}
                      onClick={() => savePageMutation.mutate()}
                    >
                      Save
                    </LoadingButton>
                  )}
                  {canShare && page?.access === 'owner' && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowShareModal(true)}
                    >
                      <FiShare2 /> Share
                    </button>
                  )}
                  {canManage && page?.access === 'owner' && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={async () => {
                        if (await appConfirm('Delete this page?')) deletePageMutation.mutate();
                      }}
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              </div>

              {mode === 'edit' && canEditPage ? (
                <div className="board-editor">
                  <input
                    className="board-title-input"
                    value={draftTitle}
                    onChange={(e) => {
                      setDraftTitle(e.target.value);
                      setDirty(true);
                    }}
                    placeholder="Page title"
                  />
                  <textarea
                    className="board-content-input"
                    value={draftContent}
                    onChange={(e) => {
                      setDraftContent(e.target.value);
                      setDirty(true);
                    }}
                    placeholder="Write Markdown… headings, lists, links, and code blocks are supported."
                    spellCheck
                  />
                  <p className="board-editor-hint">
                    Markdown wiki page
                    {page?.modifiedon_timestamp
                      ? ` · Updated ${formatLocalDateTime(page.modifiedon_timestamp)}`
                      : ''}
                    {pageFetching ? ' · Syncing…' : ''}
                    {dirty ? ' · Unsaved changes' : ''}
                  </p>
                </div>
              ) : (
                <article className="board-preview">
                  <h1 className="board-preview-title">{draftTitle || page?.title}</h1>
                  <div
                    className="board-preview-body"
                    dangerouslySetInnerHTML={{ __html: previewHtml || '<p><em>Empty page</em></p>' }}
                  />
                </article>
              )}
            </>
          )}
        </main>
      </div>

      <Modal
        open={showNotebookModal}
        onClose={() => setShowNotebookModal(false)}
        ariaLabel="New notebook"
      >
        <h2>New notebook</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createNotebookMutation.mutate({ title: notebookTitle.trim() });
          }}
        >
          <div className="form-group">
            <label htmlFor="board-notebook-title">Title</label>
            <input
              id="board-notebook-title"
              value={notebookTitle}
              onChange={(e) => setNotebookTitle(e.target.value)}
              placeholder="Team wiki"
              required
              autoFocus
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowNotebookModal(false)}>
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={createNotebookMutation.isLoading}
              icon={FiPlus}
            >
              Create
            </LoadingButton>
          </div>
        </form>
      </Modal>

      <Modal
        open={showSectionModal}
        onClose={() => setShowSectionModal(false)}
        ariaLabel="New section"
      >
        <h2>New section</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createSectionMutation.mutate({
              notebookId: selectedNotebookId,
              title: sectionTitle.trim(),
            });
          }}
        >
          <div className="form-group">
            <label>Title</label>
            <input
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              placeholder="Processes"
              required
              autoFocus
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowSectionModal(false)}>
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={createSectionMutation.isLoading}
              icon={FiPlus}
            >
              Create
            </LoadingButton>
          </div>
        </form>
      </Modal>

      <Modal open={showShareModal} onClose={() => setShowShareModal(false)} ariaLabel="Share page">
        <h2>
          <FiUsers aria-hidden /> Share page
        </h2>
        <p className="board-share-intro">
          Share <strong>{page?.title}</strong> with users in the app. They will see it under Board.
        </p>
        <form
          className="board-share-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!shareUserId) return;
            addShareMutation.mutate();
          }}
        >
          <div className="form-group">
            <label>User</label>
            <select value={shareUserId} onChange={(e) => setShareUserId(e.target.value)} required>
              <option value="">Select user…</option>
              {shareableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {userLabel(u)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Access</label>
            <select value={shareAccess} onChange={(e) => setShareAccess(e.target.value)}>
              <option value="view">Can view</option>
              <option value="edit">Can edit</option>
            </select>
          </div>
          <LoadingButton
            type="submit"
            className="btn btn-primary"
            loading={addShareMutation.isLoading}
            icon={FiShare2}
          >
            Add share
          </LoadingButton>
        </form>

        <ul className="board-share-list">
          {(shares || []).length === 0 ? (
            <li className="board-share-empty">Not shared with anyone yet.</li>
          ) : (
            shares.map((s) => (
              <li key={s.id}>
                <div>
                  <strong>{userLabel(s)}</strong>
                  <span className="board-share-access">{s.access}</span>
                </div>
                <button
                  type="button"
                  className="board-icon-btn"
                  title="Remove share"
                  onClick={() => removeShareMutation.mutate(s.user_id)}
                >
                  <FiX />
                </button>
              </li>
            ))
          )}
        </ul>

        {canManage && selectedNotebook?.is_owner && (
          <div className="board-danger-zone">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={async () => {
                if (
                  await appConfirm(
                    'Delete this entire notebook and all of its sections/pages? This cannot be undone.'
                  )
                ) {
                  deleteNotebookMutation.mutate(selectedNotebookId);
                  setShowShareModal(false);
                }
              }}
            >
              <FiTrash2 /> Delete notebook
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Board;
